import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, RefreshCw, Send, Circle, Play, Clock, TrendingUp, TrendingDown, Minus, AlertTriangle } from 'lucide-react';
import { useState } from 'react';
import { RunPipelineNow } from './RunPipelineNow';

// Human-readable duration: 1047 -> "17m 27s", 14.2 -> "14.2s", 0.8 -> "800ms"
function fmtDur(s) {
  if (s == null) return null;
  if (s >= 60) return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
  if (s >= 1)  return `${s.toFixed(1)}s`;
  return `${Math.round(s * 1000)}ms`;
}

// Compute trend between last vs previous duration: returns label + colour + icon
function durationTrend(last, prev) {
  if (last == null || prev == null || prev <= 0) return null;
  const deltaPct = ((last - prev) / prev) * 100;
  const deltaS = last - prev;
  // Only flag as "trend" if the change is ≥10% AND ≥2s of absolute drift — filters out noise.
  if (Math.abs(deltaPct) < 10 || Math.abs(deltaS) < 2) {
    return { label: 'stable', colour: 'text-gray-500 bg-gray-50', Icon: Minus };
  }
  if (deltaS > 0) {
    // Slower is bad — up-arrow in red/amber
    const severity = deltaPct >= 30 ? 'text-red-600 bg-red-50' : 'text-amber-600 bg-amber-50';
    return { label: `+${fmtDur(deltaS)} slower`, colour: severity, Icon: TrendingUp };
  }
  return {
    label: `${fmtDur(Math.abs(deltaS))} faster`,
    colour: 'text-green-600 bg-green-50',
    Icon: TrendingDown,
  };
}

function JobCard({ id, job, onTrigger }) {
  const [triggering, setTriggering] = useState(false);
  const stats = job.last_stats;
  const trend = durationTrend(job.last_duration_s, job.prev_duration_s);

  const handleTrigger = async () => {
    setTriggering(true);
    try { await onTrigger(id); } catch { }
    setTimeout(() => setTriggering(false), 2000);
  };

  return (
    <div data-testid={`scheduler-job-${id}`} className="bg-white rounded-2xl border border-gray-100 p-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-bold text-gray-500 tracking-widest uppercase">{job.label}</p>
        <div className="flex items-center gap-2">
          <button
            data-testid={`trigger-job-${id}`}
            onClick={handleTrigger}
            disabled={triggering}
            className={`text-[10px] font-bold px-2.5 py-1 rounded-lg flex items-center gap-1 transition-all
              ${triggering
                ? 'bg-green-100 text-green-600'
                : 'bg-blue-50 text-blue-600 hover:bg-blue-100 active:scale-95'}`}
          >
            {triggering ? <Loader2 size={10} className="animate-spin" /> : <Play size={10} className="fill-current" />}
            {triggering ? 'Running...' : 'Run'}
          </button>
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-lg flex items-center gap-1 ${job.running ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
            <Circle size={6} className={job.running ? 'fill-green-500' : 'fill-red-500'} />
            {job.running ? 'Active' : 'Stopped'}
          </span>
        </div>
      </div>

      {/* Missed-run banner — top priority alert (shows before anything else) */}
      {job.missed_runs && job.days_since_last_run != null && (
        <div data-testid={`job-missed-banner-${id}`} className="mb-2 flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200">
          <AlertTriangle size={14} className="text-red-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-[11px] font-bold text-red-700 leading-tight">
              Hasn't run in {job.days_since_last_run < 1 ? 'over 24h' : `${Math.floor(job.days_since_last_run)} days`}
            </p>
            <p className="text-[9px] text-red-500 mt-0.5">Check the error below or click Run to investigate.</p>
          </div>
        </div>
      )}
      <div className="space-y-1.5">
        {job.interval && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Interval</span>
            <span className="text-xs font-semibold text-gray-800 bg-gray-50 px-2 py-0.5 rounded-lg">{job.interval}</span>
          </div>
        )}
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Next Run</span>
          <span className="text-xs text-gray-500">{job.next_run ? new Date(job.next_run).toLocaleTimeString() : 'N/A'}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Last Run</span>
          <span className="text-xs text-gray-500">{job.last_run ? new Date(job.last_run).toLocaleString() : 'Never'}</span>
        </div>
        {job.last_duration_s != null && (
          <div className="flex items-center justify-between" data-testid={`job-duration-${id}`}>
            <span className="text-sm text-gray-600 flex items-center gap-1">
              <Clock size={11} className="text-gray-400" /> Duration
            </span>
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-semibold text-gray-700 tabular-nums">{fmtDur(job.last_duration_s)}</span>
              {trend && (
                <span
                  data-testid={`job-trend-${id}`}
                  className={`text-[9px] font-bold px-1.5 py-0.5 rounded flex items-center gap-0.5 ${trend.colour}`}
                  title={job.prev_duration_s != null ? `Previous run: ${fmtDur(job.prev_duration_s)}` : ''}
                >
                  <trend.Icon size={9} />
                  {trend.label}
                </span>
              )}
            </div>
          </div>
        )}
        {job.last_error && (
          <div className="mt-1.5 px-3 py-2 rounded-lg bg-red-50 border border-red-100">
            <p className="text-[10px] font-bold text-red-600 uppercase tracking-wider">Last Error</p>
            <p className="text-[11px] text-red-500 mt-0.5">{job.last_error.message}</p>
            <p className="text-[9px] text-red-400 mt-0.5">{job.last_error.time ? new Date(job.last_error.time).toLocaleString() : ''}</p>
          </div>
        )}
      </div>
      {stats && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <p className="text-[10px] font-bold text-gray-400 tracking-widest uppercase mb-2">Last Run Results</p>
          <div className="grid grid-cols-4 gap-2 text-center">
            <div className="bg-blue-50 rounded-lg py-1.5 px-1">
              <p className="text-sm font-extrabold text-blue-700">{stats.l1_exact}</p>
              <p className="text-[8px] font-bold text-blue-500 uppercase">L1 Exact</p>
            </div>
            <div className="bg-violet-50 rounded-lg py-1.5 px-1">
              <p className="text-sm font-extrabold text-violet-700">{stats.l2_merged}</p>
              <p className="text-[8px] font-bold text-violet-500 uppercase">L2 Merged</p>
            </div>
            <div className="bg-amber-50 rounded-lg py-1.5 px-1">
              <p className="text-sm font-extrabold text-amber-700">{stats.l3_grey}</p>
              <p className="text-[8px] font-bold text-amber-500 uppercase">L3 Grey</p>
            </div>
            <div className="bg-green-50 rounded-lg py-1.5 px-1">
              <p className="text-sm font-extrabold text-green-700">{stats.new_added}</p>
              <p className="text-[8px] font-bold text-green-500 uppercase">New</p>
            </div>
          </div>
          <p className="text-[9px] text-gray-400 mt-1.5 text-right">{stats.total_incoming} total incoming</p>
        </div>
      )}
    </div>
  );
}

export function SchedulerTab({ schedulerData, loading, refreshing, onManualRefresh, onTriggerJob }) {
  if (loading) return <div className="text-center py-10"><Loader2 size={24} className="animate-spin mx-auto text-gray-400" /></div>;
  if (!schedulerData) return null;

  const jobs = schedulerData.jobs || {};
  const jobEntries = Object.entries(jobs);

  return (
    <div className="mt-2 space-y-3">
      {/* One-click full pipeline refresh */}
      <RunPipelineNow />

      {/* Global status bar */}
      <div className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-2">
        <span className="text-xs font-medium text-gray-500">Scheduler Engine</span>
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-lg ${schedulerData.scheduler_running ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
          {schedulerData.scheduler_running ? 'Running' : 'Stopped'}
        </span>
      </div>

      {/* All jobs */}
      {jobEntries.map(([id, job]) => (
        <JobCard key={id} id={id} job={job} onTrigger={onTriggerJob} />
      ))}

      <button data-testid="manual-refresh-btn" onClick={onManualRefresh} disabled={refreshing}
        className="w-full mt-1 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-blue-700 disabled:opacity-60 transition-all">
        {refreshing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Refresh Now
      </button>
    </div>
  );
}

export function PublisherTab({ publishedCards, loading, refreshing, onPublishNow }) {
  return (
    <div className="mt-2 space-y-3">
      <button data-testid="publish-now-btn" onClick={onPublishNow} disabled={refreshing}
        className="w-full py-2.5 rounded-xl bg-gray-900 text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-gray-800 disabled:opacity-60 transition-all">
        {refreshing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Publish Top Cards Now
      </button>
      {loading ? <div className="text-center py-10"><Loader2 size={24} className="animate-spin mx-auto text-gray-400" /></div> : (
        publishedCards.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-sm text-gray-400">No cards published yet</p>
            <p className="text-xs text-gray-300 mt-1">Cards with score 70+ will be auto-published every 30 min</p>
          </div>
        ) : (
          <div className="space-y-2">
            {publishedCards.map(card => (
              <div key={card.id} className="bg-white rounded-xl border border-gray-100 p-3">
                <p className="text-sm font-medium text-gray-900 truncate">{card.topic_title}</p>
                <div className="flex items-center gap-2 mt-1.5">
                  {Object.entries(card.platforms || {}).map(([platform, result]) => (
                    <span key={platform} className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase
                      ${result.status === 'published' ? 'bg-green-50 text-green-600'
                        : result.status === 'skipped' ? 'bg-yellow-50 text-yellow-600'
                        : 'bg-red-50 text-red-600'}`}>
                      {platform}: {result.status}
                    </span>
                  ))}
                </div>
                <p className="text-[10px] text-gray-400 mt-1">{new Date(card.published_at).toLocaleString()}</p>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}
